import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { X, Trash2, Pencil, Calendar as CalendarIcon } from "lucide-react";
import { api, getFresh, fmt12, fmtDMY, todayStr } from "@/lib/api";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

const DAY_LABELS = ["S", "M", "T", "W", "T", "F", "S"];
const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
export const ALL_DAYS = [0, 1, 2, 3, 4, 5, 6];

const formOf = (m) => ({
  name: m?.name || "",
  times: m?.times || [],
  notes: m?.notes || "",
  group: m?.group || "",
  days: m?.days || ALL_DAYS,
  start_date: m?.start_date || m?.created_at?.slice(0, 10) || todayStr(),
  end_date: m?.end_date || "",
});

const DateField = ({ value, placeholder, onChange, testid, disabledBefore }) => {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid={testid}
          className="flex h-12 w-full items-center gap-2 overflow-hidden rounded-xl border border-slate-800 bg-slate-900/70 px-3 text-left"
        >
          <CalendarIcon className="h-[18px] w-[18px] shrink-0 text-sky-300" />
          <span className={`truncate text-[14px] ${value ? "text-slate-100" : "text-slate-500"}`}>
            {value ? fmtDMY(value) : placeholder}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto border-slate-800 bg-slate-950 p-0">
        <Calendar
          mode="single"
          selected={value ? new Date(value + "T12:00:00") : undefined}
          defaultMonth={value ? new Date(value + "T12:00:00") : undefined}
          disabled={disabledBefore ? { before: new Date(disabledBefore + "T12:00:00") } : undefined}
          onSelect={(d) => {
            if (d) onChange(todayStr(d));
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
};

/** One group bubble: tap to pick, pencil to rename in place. */
const GroupChip = ({ name, selected, onPick, onEdit }) => (
  <span
    className={`flex select-none items-center gap-1 rounded-full border py-1.5 pl-3 pr-2 text-[13px] transition-colors duration-200 ${
      selected
        ? "border-sky-500/40 bg-sky-500/15 text-sky-200"
        : "border-slate-700 bg-slate-900 text-slate-400"
    }`}
  >
    <button data-testid={`pick-group-${name}`} onClick={onPick} className="max-w-[140px] truncate">
      {name}
    </button>
    <button data-testid={`edit-group-${name}`} onClick={onEdit} className="pl-0.5 text-sky-300/70">
      <Pencil className="h-3 w-3" />
    </button>
  </span>
);

/** Group bubbles: tap to pick, pencil to rename in place, "+ New group" types in its own bubble. */
const GroupPicker = ({ groups, value, onChange, onRenamed, onEditingChange }) => {
  const [order, setOrder] = useState(groups);
  const [editing, setEditingState] = useState(null); // group name, or "__new__"
  const [draft, setDraft] = useState("");
  const cancelRef = useRef(false);
  const setEditing = (v) => {
    setEditingState(v);
    onEditingChange?.(!!v);
  };

  useEffect(() => {
    setOrder(groups);
  }, [groups]);

  const commit = async () => {
    const name = draft.trim();
    const target = editing;
    setEditing(null);
    if (cancelRef.current) {
      cancelRef.current = false;
      return;
    }
    if (!name || !target) return;
    if (target === "__new__") {
      if (!order.includes(name)) {
        try {
          await api.post("/groups", { name });
          onRenamed?.();
        } catch {
          toast.error("Could not create the group");
          return;
        }
      }
      onChange(name);
      return;
    }
    if (name === target) return;
    try {
      await api.patch("/groups", { name: target, new_name: name });
      onRenamed?.();
    } catch {
      toast.error("Could not rename the group");
      return;
    }
    if (value === target) onChange(name);
  };

  const bubbleInput = (testid) => (
    <input
      data-testid={testid}
      autoFocus
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          e.stopPropagation();
          e.currentTarget.blur();
        }
        if (e.key === "Escape") {
          e.preventDefault();
          e.stopPropagation();
          cancelRef.current = true;
          e.currentTarget.blur();
        }
      }}
      onBlur={commit}
      placeholder="Group name"
      className="w-32 rounded-full border border-sky-500/50 bg-slate-900 px-3 py-1.5 text-[13px] text-slate-100 outline-none"
    />
  );

  return (
    <div>
      <p className="text-[13px] text-slate-500">Group (optional)</p>
      <div className="mt-1.5 flex flex-wrap items-center gap-1.5" data-testid="group-chip-row">
        {order.map((g) =>
          editing === g ? (
            <span key={g}>{bubbleInput(`group-chip-input-${g}`)}</span>
          ) : (
            <GroupChip
              key={g}
              name={g}
              selected={value === g}
              onPick={() => onChange(value === g ? "" : g)}
              onEdit={() => {
                setDraft(g);
                setEditing(g);
              }}
            />
          ),
        )}
        {editing === "__new__" ? (
          bubbleInput("medicine-group-input")
        ) : (
          <button
            data-testid="new-group-btn"
            onClick={() => {
              setDraft("");
              setEditing("__new__");
            }}
            className="rounded-full border border-dashed border-slate-700 px-3 py-1.5 text-[13px] text-slate-400"
          >
            + New group
          </button>
        )}
      </div>
    </div>
  );
};

/** Shared create/edit form. `onSaved` / `onDeleted` let the host refresh or close. */
export const MedicineForm = ({ med, groups = [], onSaved, onDeleted, onGroupsChanged, onInlineEditChange }) => {
  const editing = med || null;
  const [form, setForm] = useState(() => formOf(med));
  const [newTime, setNewTime] = useState("08:00");
  const [editTime, setEditTime] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    setForm(formOf(med));
  }, [med]);

  const retime = async () => {
    if (!editTime?.to) return;
    const { from, to } = editTime;
    if (to === from) return setEditTime(null);
    if (form.times.includes(to)) {
      setEditTime(null);
      return toast.error("This medicine already has that time");
    }
    const next = [...form.times.filter((t) => t !== from), to].sort();
    setEditTime(null);
    if (!editing) {
      setForm((f) => ({ ...f, times: next }));
      return;
    }
    try {
      await api.post(`/medicines/${editing.med_id}/retime`, { from_time: from, to_time: to });
      setForm((f) => ({ ...f, times: next }));
      toast.success("Time changed — its history moved with it");
      onSaved?.({ keepOpen: true });
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not change the time");
    }
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Enter a medicine name");
    // no times picked yet: fall back to whatever the time input is showing
    const times = form.times.length ? form.times : newTime ? [newTime] : [];
    if (times.length === 0) return toast.error("Add at least one time");
    if (form.days.length === 0) return toast.error("Pick at least one day");
    if (!form.start_date) return toast.error("Pick a start date");
    if (form.end_date && form.end_date < form.start_date)
      return toast.error("End date can't be before the start date");
    const payload = {
      name: form.name,
      times,
      notes: form.notes,
      group: form.group.trim() || null,
      days: form.days,
      start_date: form.start_date,
      end_date: form.end_date || null,
    };
    try {
      if (editing) await api.put(`/medicines/${editing.med_id}`, payload);
      else await api.post("/medicines", payload);
      onSaved?.({});
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not save");
    }
  };

  const remove = async () => {
    setConfirmDelete(false);
    try {
      await api.delete(`/medicines/${editing.med_id}`);
      await getFresh("/medicines");
      onDeleted?.();
    } catch {
      toast.error("Could not delete");
    }
  };

  return (
    <div className="min-w-0 space-y-2.5">
      <Input
        data-testid="medicine-name-input"
        value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })}
        placeholder="Medicine name"
        className="h-12 rounded-xl border-slate-800 bg-slate-900/70 text-[15px]"
      />
      <GroupPicker
        groups={groups}
        value={form.group}
        onChange={(g) => setForm((f) => ({ ...f, group: g }))}
        onRenamed={onGroupsChanged}
        onEditingChange={onInlineEditChange}
      />
      <Input
        data-testid="medicine-notes-input"
        value={form.notes}
        onChange={(e) => setForm({ ...form, notes: e.target.value })}
        placeholder="Note (optional)"
        className="h-12 rounded-xl border-slate-800 bg-slate-900/70 text-[15px]"
      />
      <div className="flex gap-2">
        <Input
          data-testid="medicine-time-input"
          type="time"
          value={newTime}
          onChange={(e) => setNewTime(e.target.value)}
          className="h-12 flex-1 rounded-xl border-slate-800 bg-slate-900/70 text-[15px]"
        />
        <button
          data-testid="add-time-btn"
          onClick={() => {
            if (!newTime || form.times.includes(newTime)) return;
            setForm((f) => ({ ...f, times: [...f.times, newTime].sort() }));
          }}
          className="h-12 shrink-0 rounded-xl border border-sky-500/30 bg-sky-500/10 px-4 text-[13px] font-semibold text-sky-300 active:scale-95"
        >
          Add time
        </button>
      </div>
      {form.times.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {form.times.map((t) => (
            <button
              key={t}
              data-testid={`edit-time-${t}`}
              onClick={() => setEditTime({ from: t, to: t })}
              className="flex items-center gap-1.5 rounded-full border border-slate-700 bg-slate-900 px-3 py-1.5 text-[13px] text-slate-300"
            >
              {fmt12(t)} <Pencil className="h-3 w-3 text-sky-300/80" />
            </button>
          ))}
        </div>
      )}
      <div>
        <div className="flex items-center justify-between">
          <p className="text-[13px] text-slate-500">Repeats on</p>
          <button
            data-testid="days-every-day-btn"
            onClick={() => setForm((f) => ({ ...f, days: ALL_DAYS }))}
            className="text-[13px] text-sky-300/90"
          >
            Every day
          </button>
        </div>
        <div className="mt-1.5 flex gap-1.5">
          {DAY_LABELS.map((lbl, idx) => {
            const on = form.days.includes(idx);
            return (
              <button
                key={idx}
                data-testid={`day-toggle-${idx}`}
                aria-label={DAY_NAMES[idx]}
                onClick={() =>
                  setForm((f) => ({
                    ...f,
                    days: on ? f.days.filter((d) => d !== idx) : [...f.days, idx].sort(),
                  }))
                }
                className={`h-11 flex-1 rounded-xl text-[14px] transition-all duration-200 ${
                  on
                    ? "bg-sky-500 font-bold text-slate-950 shadow-[0_0_12px_rgba(56,189,248,0.35)]"
                    : "border border-dashed border-slate-700 bg-transparent font-medium text-slate-600"
                }`}
              >
                {lbl}
              </button>
            );
          })}
        </div>
        <p className="mt-1 text-[12px] text-slate-500" data-testid="days-summary">
          {form.days.length === 7
            ? "Every day"
            : form.days.length === 0
              ? "No days selected"
              : form.days.map((d) => DAY_NAMES[d]).join(", ")}
        </p>
      </div>
      <div>
        <div className="flex items-center justify-between">
          <p className="text-[13px] text-slate-500">Start date · end date (optional)</p>
          {form.end_date && (
            <button
              data-testid="clear-dates-btn"
              onClick={() => setForm((f) => ({ ...f, end_date: "" }))}
              className="text-[13px] text-sky-300/90"
            >
              No end date
            </button>
          )}
        </div>
        <div className="mt-1.5 space-y-2">
          <DateField
            testid="medicine-start-date-input"
            value={form.start_date}
            placeholder="Start date"
            onChange={(v) =>
              setForm((f) => ({ ...f, start_date: v, end_date: f.end_date && f.end_date < v ? "" : f.end_date }))
            }
          />
          <DateField
            testid="medicine-end-date-input"
            value={form.end_date}
            placeholder="No end date"
            disabledBefore={form.start_date}
            onChange={(v) => setForm((f) => ({ ...f, end_date: v }))}
          />
        </div>
      </div>
      <button
        data-testid="save-medicine-btn"
        onClick={save}
        className="h-12 w-full rounded-full bg-gradient-to-r from-sky-500 to-blue-600 text-[15px] font-semibold text-white active:scale-95"
      >
        {editing ? "Save" : "Add"}
      </button>
      {editing && (
        <button
          data-testid="delete-medicine-btn"
          onClick={() => setConfirmDelete(true)}
          className="flex h-12 w-full items-center justify-center gap-2 rounded-full text-[15px] font-semibold text-red-400/90 transition-colors duration-200 hover:bg-red-500/10 active:scale-95"
        >
          <Trash2 className="h-4 w-4" /> Delete medicine
        </button>
      )}

      <Dialog open={!!editTime} onOpenChange={(o) => !o && setEditTime(null)}>
        <DialogContent className="max-w-[340px] rounded-2xl border-slate-800 bg-slate-950/95 backdrop-blur-xl">
          <DialogHeader className="text-left">
            <DialogTitle className="text-lg">Change {fmt12(editTime?.from)}</DialogTitle>
            <DialogDescription className="text-[14px] text-slate-400">
              The dose history logged at this time moves to the new time.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2">
            <Input
              data-testid="edit-time-input"
              type="time"
              value={editTime?.to || ""}
              onChange={(e) => setEditTime((s) => ({ ...s, to: e.target.value }))}
              className="h-12 flex-1 rounded-xl border-slate-800 bg-slate-900/70 text-[15px]"
            />
            <button
              data-testid="save-time-btn"
              onClick={retime}
              className="h-12 shrink-0 rounded-xl bg-gradient-to-r from-sky-500 to-blue-600 px-5 text-[14px] font-semibold text-white active:scale-95"
            >
              Update
            </button>
          </div>
          <button
            data-testid={`remove-time-${editTime?.from}`}
            onClick={() => {
              setForm((f) => ({ ...f, times: f.times.filter((x) => x !== editTime.from) }));
              setEditTime(null);
            }}
            className="mt-1 flex h-11 w-full items-center justify-center gap-2 rounded-full text-[14px] font-semibold text-red-400/90 hover:bg-red-500/10 active:scale-95"
          >
            <X className="h-4 w-4" /> Remove this time
          </button>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent className="max-w-[340px] rounded-2xl border-slate-800 bg-slate-950/95 backdrop-blur-xl">
          <DialogHeader className="text-left">
            <DialogTitle className="text-lg">Delete {editing?.name}?</DialogTitle>
            <DialogDescription className="text-[15px] text-slate-400">
              Its past logs are removed too. This can&apos;t be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2">
            <button
              data-testid="cancel-delete-btn"
              onClick={() => setConfirmDelete(false)}
              className="h-12 flex-1 rounded-full border border-slate-700 text-[15px] font-semibold text-slate-300 active:scale-95"
            >
              Cancel
            </button>
            <button
              data-testid="confirm-delete-btn"
              onClick={remove}
              className="h-12 flex-1 rounded-full bg-red-500/15 text-[15px] font-semibold text-red-300 ring-1 ring-red-500/30 active:scale-95"
            >
              Delete
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
